// The Work composer's routine launch: one launch per routine at a time, held
// across the route change into the run it started. The run itself is the app's
// routine run mutation, which the binding hands in, so nothing here reads the
// Routines zone; the route crosses as `onNavigate`.
import type { NavigatePort } from '@heidi/runtime/shell';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { useRoutineLaunchLock } from '../workflows/routines/useRoutineLaunchLock';
import { clearComposerLinkedItemsAtom } from './artifacts/state';

/** What the clinician added next to the routine chip, sent with the launch. */
export interface RoutineLaunchInput {
  prompt?: string;
  attachmentIds?: string[];
  sessionId?: string;
  patientProfileId?: string;
}

/** The run a composer launch asks for: the routine and what rides with its chip. */
export interface ComposerRoutineRun {
  workflowId: string;
  prompt?: string;
  attachment_ids?: string[];
  session_id?: string;
  patient_profile_id?: string;
  trigger_source: 'work_composer_play';
  trigger_type: 'on_demand';
}

/** Runs a routine and reports the run's project, if it opened one. */
export type RunComposerRoutine = (
  run: ComposerRoutineRun,
  callbacks: {
    onSuccess: (result: { project_id?: string | null }) => void;
    onError: () => void;
  }
) => void;

export interface RoutineLaunchOperationOptions {
  runRoutine: RunComposerRoutine;
  /** Opens the run the launch started. */
  onNavigate: NavigatePort;
  onLaunchingChange?: (workflowId: string | null) => void;
}

export const useRoutineLaunchOperation = ({
  runRoutine,
  onNavigate,
  onLaunchingChange,
}: RoutineLaunchOperationOptions): ((
  workflowId: string,
  input?: RoutineLaunchInput
) => void) => {
  const launchLock = useRoutineLaunchLock();
  const clearLinkedItems = useSetAtom(clearComposerLinkedItemsAtom);

  return useCallback(
    (workflowId: string, input: RoutineLaunchInput = {}) => {
      // Auto-repeat and double-clicks outrun the mutation's own isPending.
      if (!launchLock.tryBegin(workflowId)) return;
      onLaunchingChange?.(workflowId);
      // The lock is held across the route change so a second play cannot start one.
      const release = () => {
        launchLock.end(workflowId);
        onLaunchingChange?.(null);
      };
      runRoutine(
        {
          workflowId,
          prompt: input.prompt,
          attachment_ids: input.attachmentIds,
          session_id: input.sessionId,
          patient_profile_id: input.patientProfileId,
          trigger_source: 'work_composer_play',
          trigger_type: 'on_demand',
        },
        {
          onSuccess: (res) => {
            if (!res.project_id) {
              release();
              return;
            }
            clearLinkedItems();
            void Promise.resolve(
              onNavigate(`/scribe/chat/projects/${res.project_id}`)
            ).then(release, release);
          },
          onError: release,
        }
      );
    },
    [clearLinkedItems, launchLock, onLaunchingChange, onNavigate, runRoutine]
  );
};
